import { useMemo } from 'react';

import { useProjection } from '@/hooks/useProjection';
import { formatCompact, formatNumber } from '@/lib/format';
import { useSimulatorState } from '@/store/useSimulatorState';

import { CashFlowChart } from '@/components/charts/CashFlowChart';
import { ChartCard } from '@/components/charts/ChartCard';
import { ClientsByTierChart } from '@/components/charts/ClientsByTierChart';
import { CostsRevenuesChart } from '@/components/charts/CostsRevenuesChart';
import { MrrChart } from '@/components/charts/MrrChart';

function formatBreakEven(month: number | null, horizonMonths: number): string {
  if (month === null) return `non atteint sur ${horizonMonths} mois`;
  return `mois ${month}`;
}

export function ChartsArea() {
  const tiers = useSimulatorState((s) => s.tiers);
  const horizonMonths = useSimulatorState((s) => s.projection.horizonMonths);
  const { snapshots, operationalBreakEvenMonth, globalBreakEvenMonth, finalKpis } = useProjection();

  const mrrData = useMemo(
    () => snapshots.map((s) => ({ month: s.month, mrr: s.mrr })),
    [snapshots],
  );

  const costsData = useMemo(
    () =>
      snapshots.map((s) => ({
        month: s.month,
        mrr: s.mrr,
        cost: s.monthlyCost,
        // Revenu total mensuel = MRR + marge nette sur les frais d'entrée (matériel).
        total: s.mrr + s.oneTimeRevenueThisMonth - s.oneTimeCostThisMonth,
      })),
    [snapshots],
  );

  const hasEntryMargin = tiers.some((t) => (t.entryPrice ?? 0) > 0 || (t.entryCost ?? 0) > 0);

  const cashFlowData = useMemo(
    () => snapshots.map((s) => ({ month: s.month, cashFlow: s.cashFlow })),
    [snapshots],
  );

  const mrrDesc = `MRR — évolution de 0 à ${formatCompact(finalKpis.mrr)} sur ${horizonMonths} mois`;
  const costsDesc =
    `Revenus vs coûts mensuels sur ${horizonMonths} mois. ` +
    `Break-even opérationnel : ${formatBreakEven(operationalBreakEvenMonth, horizonMonths)}`;
  const clientsDesc = `Clients actifs par palier — ${formatNumber(Math.round(finalKpis.totalClients))} clients au mois ${horizonMonths}`;
  const cashFlowDesc =
    `Cash flow cumulé sur ${horizonMonths} mois. ` +
    `Break-even global : ${formatBreakEven(globalBreakEvenMonth, horizonMonths)}`;

  if (tiers.length === 0) {
    return (
      <div
        role="status"
        className="flex items-center justify-center rounded-lg border border-dashed p-12 text-sm text-muted-foreground"
      >
        Ajoutez au moins un palier tarifaire pour visualiser les projections.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 print:grid-cols-1">
      <ChartCard title="MRR" description={mrrDesc}>
        <MrrChart data={mrrData} />
      </ChartCard>
      <ChartCard title="Coûts vs Revenus" description={costsDesc}>
        <CostsRevenuesChart
          data={costsData}
          operationalBreakEvenMonth={operationalBreakEvenMonth}
          showTotal={hasEntryMargin}
        />
      </ChartCard>
      <ChartCard title="Clients par palier" description={clientsDesc}>
        <ClientsByTierChart snapshots={snapshots} tiers={tiers} />
      </ChartCard>
      <ChartCard title="Cash flow cumulé" description={cashFlowDesc}>
        <CashFlowChart data={cashFlowData} globalBreakEvenMonth={globalBreakEvenMonth} />
      </ChartCard>
    </div>
  );
}
